import { ApolloQueryResult, useMutation, useQuery } from "@apollo/client";
import * as Dialog from "@radix-ui/react-dialog";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import CustomSelect from "src/components/CustomSelect";
import Icon from "src/components/Icon";
import { Spinner } from "src/components/Spinner";
import { UpdateInviteList } from "src/graphql/mutations";
import { AddGuestToInviteList } from "src/graphql/mutations/invitation.mutation";
import { NumberOfGuestsByCategory } from "src/graphql/queries";
import { Trash } from "src/pages/aso-ebi/Icons";
import RemoveGuest from "src/pages/invitations/invite-list/modals/RemoveGuest";
import { useIsDesktopInvite } from "src/pages/wallet/CashGifts/useDesktop";
import useAppStore from "src/store/utils";
import {
  addGuestToInviteList,
  addGuestToInviteListVariables,
  filterInvitationListBy,
  filterInvitationListByVariables,
  filterInvitationListBy_filterInvitationListBy,
  invitationListFilter,
  noGuestPerCategory,
  updateInviteList,
} from "src/types/api.d";
// import * as XLSX from "xlsx";

type Cell = {
  value: string;
  error: string | null;
};

type Row = [Cell, Cell, Cell, Cell, Cell];

const initialRow = (): Row => [
  { value: "", error: null }, // First Name
  { value: "", error: null }, // Last Name
  { value: "", error: null }, // Email
  { value: "", error: null }, // Phone
  { value: "", error: null }, // Category
];

const isRowComplete = (row: Row) =>
  row[0].value.trim() !== "" &&
  row[1].value.trim() !== "" &&
  row[3].value.trim() !== "" &&
  row[4].value.trim() !== "";

interface Props {
  inviteList: filterInvitationListBy | undefined;
  refetch: (
    variables?: Partial<filterInvitationListByVariables> | undefined
  ) => Promise<ApolloQueryResult<filterInvitationListBy>>;
}

const InviteSheet = ({ inviteList, refetch }: Props) => {
  const { event } = useAppStore((state) => state);

  const navigate = useNavigate();

  const [rowIds, setRowIds] = useState<(number | null)[]>([]);

  // const [rowIds, setRowIds] = useState<(number | null)[]>(() => {
  //   if (!event.id) {
  //     console.warn("Event ID is undefined, initializing with default rowIds.");
  //     return Array.from({ length: isDesktop ? 10 : 5 }, () => null);
  //   }

  //   const local = localStorage.getItem(`inviteTableDraft_${event.id}`);
  //   if (local) {
  //     try {
  //       const parsed = JSON.parse(local);
  //       if (typeof parsed === "object" && !Array.isArray(parsed)) {
  //         const unsavedRows = Object.values(parsed) as {
  //           row: Row;
  //           rowId: number | null;
  //         }[];
  //         const ids = unsavedRows.map(({ rowId }) => rowId);
  //         const minLength = isDesktop ? 10 : 5;
  //         const needed = Math.max(0, minLength - ids.length);
  //         return [...ids, ...Array.from({ length: needed }, () => null)];
  //       }
  //     } catch (err) {
  //       console.error("Failed to parse local table draft:", err);
  //     }
  //   }
  //   return Array.from({ length: isDesktop ? 10 : 5 }, () => null);
  // });

  const timeouts = useRef<{ [key: number]: ReturnType<typeof setTimeout> }>({});

  const [originalData, setOriginalData] = useState<Row[]>([]);

  const [updatingRowIdx, setUpdatingRowIdx] = useState<number | null>(null);

  const [openDeleteGuest, setOpenDeleteGuest] = useState<boolean>(false);

  const [guestToDelete, setGuestToDelete] =
    useState<filterInvitationListBy_filterInvitationListBy | null>(null);

  const [unsavedChanges, setUnsavedChanges] = useState<{
    [rowIdx: number]: { row: Row; rowId: number | null };
  }>({});

  // For mobile begins here
  const [openRowIdx, setOpenRowIdx] = useState<number | null>(null);

  const [mobileToastMsg, setMobileToastMsg] = useState<string | null>(null);
  const [mobileToastType, setMobileToastType] = useState<
    "success" | "error" | "loading"
  >("success");
  const [toastVisible, setToastVisible] = useState(false);

  const showToastMobile = (
    msg: string,
    type: "success" | "error" | "loading" = "success",
    duration = 3000
  ) => {
    setToastVisible(true);
    setMobileToastMsg(msg);
    setMobileToastType(type);

    if (type !== "loading") {
      setTimeout(() => setToastVisible(false), duration);
    }

    // setTimeout(() => setToastVisible(false), duration - 500);
    // setTimeout(() => setMobileToastMsg(null), duration);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  // const fileInputRef = useRef<HTMLInputElement>(null);

  // const handleImportClick = () => {
  //   fileInputRef.current?.click();
  // };

  const isDesktop = useIsDesktopInvite();

  // For mobile ends here

  const capitalizeFirst = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  //categories data
  const { data: categories } = useQuery<noGuestPerCategory>(
    NumberOfGuestsByCategory,
    {
      variables: {
        eventId: event.id,
      },
      fetchPolicy: "cache-and-network",
      onError(error) {
        if (error?.message !== "unauthenticated") {
          toast.error(
            <p className="toast">{error?.message ?? "An error occured"}</p>
          );
        } else {
          navigate("/clear");
        }
      },
    }
  );

  const [updateInviteListFn] = useMutation<updateInviteList>(UpdateInviteList);

  const getCategoryIdByName = (categoryName: string | undefined) => {
    const category = categories?.noGuestPerCategory?.find(
      (item) => item?.category_name === categoryName
    );

    return category?.category_id || null;
  };

  //add data to invite list
  const [addToInviteListFn] = useMutation<
    addGuestToInviteList,
    addGuestToInviteListVariables
  >(AddGuestToInviteList);

  // const [data, setData] = useState<Row[]>(() =>
  //   Array.from({ length: 10 }, () => initialRow())
  // );

  const [data, setData] = useState<Row[]>(() => {
    if (!event.id) {
      console.warn("Event ID is undefined, initializing with default rows.");
      return Array.from({ length: isDesktop ? 10 : 5 }, () => initialRow());
    }

    const local = localStorage.getItem(`inviteTableDraft_${event.id}`);
    if (local) {
      try {
        const parsed = JSON.parse(local);
        if (typeof parsed === "object" && !Array.isArray(parsed)) {
          const unsavedRows = Object.values(parsed) as {
            row: Row;
            rowId: number | null;
          }[];
          const rows = unsavedRows.map(({ row }) => row);
          const minLength = isDesktop ? 10 : 5;
          const needed = Math.max(0, minLength - rows.length);
          return [
            ...rows,
            ...Array.from({ length: needed }, () => initialRow()),
          ];
        }
      } catch (err) {
        console.error("Failed to parse local table draft:", err);
      }
    }
    return Array.from({ length: isDesktop ? 10 : 5 }, () => initialRow());
  });

  const validateCell = useCallback(
    (value: string, colIdx: number): string | null => {
      if (
        (colIdx === 0 || colIdx === 1 || colIdx === 3) &&
        value.trim() === ""
      ) {
        return "Required";
      }

      // Email (colIdx === 2)
      if (colIdx === 2 && value.trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value.trim())) {
          return "Invalid email";
        }
      }

      // Phone (colIdx === 3)
      if (colIdx === 3 && value.trim() !== "") {
        const phoneRegex = /^\+\d{1,3}[0-9\s\-()]{4,}$/;
        if (!phoneRegex.test(value.trim())) {
          return "Invalid phone (must start with + and country code)";
        }

        // extra: count digits to ensure at least 8 total digits
        const digitCount = value.replace(/\D/g, "").length;
        if (digitCount < 8) {
          return "Phone number must have at least 8 digits";
        }
      }

      return null;
    },
    []
  );

  const sendAddToBackend = (row: Row, rowIdx: number) => {
    setUpdatingRowIdx(rowIdx);
    if (!isDesktop) {
      showToastMobile("Adding...", "loading");
    }
    addToInviteListFn({
      variables: {
        eventId: event.id,
        guest: {
          first_name: row[0].value,
          last_name: row[1].value,
          email: row[2].value,
          phone: row[3].value,
          category_id: getCategoryIdByName(row[4].value),
        },
      },
      onCompleted() {
        if (event.id) {
          localStorage.removeItem(`inviteTableDraft_${event.id}`);
        }

        // mark that row as clean now
        const newOriginalData = [...originalData];
        newOriginalData[rowIdx] = JSON.parse(JSON.stringify(row));
        setOriginalData(newOriginalData);
        setUpdatingRowIdx(null);

        refetch();

        // Clear unsaved changes for this row
        setUnsavedChanges((prev) => {
          const { [rowIdx]: _, ...rest } = prev;
          return rest;
        });

        if (isDesktop) {
          toast.success(
            <p className="toast">
              You have successfully added a guest to the invite list.
            </p>
          );
        } else {
          hideToast();
          showToastMobile("Guest added!");
        }
      },
      onError(error) {
        setUpdatingRowIdx(null);

        if (error?.message !== "unauthenticated") {
          if (isDesktop) {
            toast.error(
              <p className="toast">
                {/* {
                  "This guest cannot be added, please make sure the details have not been entered for another guest"
                } */}
                {error?.message ?? "An error occured adding guest"}
              </p>
            );
          } else {
            hideToast();
            showToastMobile(error?.message ?? "An error occured", "error");
          }
        } else {
          navigate("/clear");
        }
      },
    });
  };

  const commitRow = (rowIdx: number) => {
    const row = data[rowIdx];

    if (isRowComplete(row) && row.every((c) => c.error === null)) {
      // don't submit again if already saved
      // only update if row content changed
      const originalRow = originalData[rowIdx];
      if (originalRow && JSON.stringify(row) === JSON.stringify(originalRow)) {
        return; // no change, skip update
      }

      if (rowIds[rowIdx]) {
        // existing row, update whole list
        const payload = data
          .map((row, idx) => ({
            guest: {
              first_name: row[0].value,
              last_name: row[1].value,
              email: row[2].value,
              phone: row[3].value,
              category_id: getCategoryIdByName(row[4].value),
            },
            id: rowIds[idx],
          }))
          .filter(
            ({ guest }) =>
              guest.first_name.trim() !== "" &&
              guest.last_name.trim() !== "" &&
              guest.phone.trim() !== "" &&
              guest.category_id
          )
          .map(({ guest, id }) => ({
            ...guest,
            id,
          }));

        setUpdatingRowIdx(rowIdx);
        if (!isDesktop) {
          showToastMobile("Saving...", "loading");
        }
        updateInviteListFn({
          variables: {
            eventId: event.id,
            invite_list: payload,
          },
          onCompleted() {
            if (event.id) {
              localStorage.removeItem(`inviteTableDraft_${event.id}`);
            }
            if (isDesktop) {
              toast.success(
                <p className="toast">Guest successfully updated</p>
              );
            } else {
              hideToast();
              showToastMobile("Changes saved!");
            }
            setUpdatingRowIdx(null);
            setOriginalData(JSON.parse(JSON.stringify(data)));

            // Clear unsaved changes for this row
            setUnsavedChanges((prev) => {
              const { [rowIdx]: _, ...rest } = prev;
              return rest;
            });
          },
          onError(error) {
            setUpdatingRowIdx(null);
            if (error?.message !== "unauthenticated") {
              if (isDesktop) {
                toast.error(
                  <p className="toast">
                    {error?.message ?? "An error occured"}
                  </p>
                );
              } else {
                // showToastMobile("Could not save guest.");
                showToastMobile(error?.message ?? "An error occured", "error");
              }
            } else {
              navigate("/clear");
            }
          },
        });
      } else {
        // new row, add single
        sendAddToBackend(row, rowIdx);
      }
    }
  };

  const handleChange = (rowIdx: number, colIdx: number, newValue: string) => {
    const newData = [...data];
    const error = validateCell(newValue, colIdx);
    newData[rowIdx][colIdx] = { value: newValue, error };
    setData(newData);

    const originalRow = originalData[rowIdx];
    const isNewRow = !rowIds[rowIdx];
    const isModified =
      isNewRow ||
      (originalRow &&
        JSON.stringify(newData[rowIdx]) !== JSON.stringify(originalRow));

    if (isModified) {
      setUnsavedChanges((prev) => ({
        ...prev,
        [rowIdx]: { row: newData[rowIdx], rowId: rowIds[rowIdx] },
      }));
    } else {
      // If row is no longer modified, remove it from unsavedChanges
      setUnsavedChanges((prev) => {
        const { [rowIdx]: _, ...rest } = prev;
        return rest;
      });
    }

    // auto-add a new row at end
    if (
      rowIdx === newData.length - 1 &&
      newData[rowIdx].some((cell) => cell.value !== "")
    ) {
      setData([...newData, initialRow()]);
      setRowIds((prev) => [...prev, null]);
    }

    // if (error) {
    //   const capturedValue = newValue;
    //   setTimeout(() => {
    //     setData((currentData) => {
    //       const updated = [...currentData];
    //       if (
    //         updated[rowIdx] &&
    //         updated[rowIdx][colIdx]?.value === capturedValue
    //       ) {
    //         updated[rowIdx][colIdx] = {
    //           ...updated[rowIdx][colIdx],
    //           error: null,
    //         };
    //       }
    //       return updated;
    //     });
    //   }, 3000);
    // }

    // if row is fully complete & valid
    // debounce after keystroke

    // if (!isDesktop) {
    if (timeouts.current[rowIdx]) {
      clearTimeout(timeouts.current[rowIdx]);
    }
    timeouts.current[rowIdx] = setTimeout(() => {
      commitRow(rowIdx);
    }, 3000);
    // }
  };

  const hydrateData = useCallback(
    (
      guests: {
        id?: number;
        first_name: string;
        last_name: string;
        email?: string;
        phone: string;
        category: string;
      }[]
    ) => {
      const hydrated = guests.map<Row>((guest) => [
        { value: guest.first_name, error: validateCell(guest.first_name, 0) },
        { value: guest.last_name, error: validateCell(guest.last_name, 1) },
        { value: guest.email ?? "", error: null },
        { value: guest.phone, error: validateCell(guest.phone, 3) },
        { value: guest.category, error: validateCell(guest.category, 4) },
      ]);

      const defaultMinRows = isDesktop ? 10 : 5;

      const needed = Math.max(0, defaultMinRows - hydrated.length);
      const padded = [
        ...hydrated,
        ...Array.from({ length: needed }, () => initialRow()),
      ];

      setData(padded);
      setOriginalData(JSON.parse(JSON.stringify(hydrated)));
      setRowIds([
        ...guests.map((g) => g.id ?? null),
        ...Array.from({ length: needed }, () => null),
      ]);
    },
    [isDesktop, validateCell]
  );

  useEffect(() => {
    if (!event.id) {
      console.warn("Event ID is undefined, skipping hydration.");
      setData(Array.from({ length: isDesktop ? 10 : 5 }, () => initialRow()));
      setRowIds(Array.from({ length: isDesktop ? 10 : 5 }, () => null));
      setOriginalData([]);
      setUnsavedChanges({});
      return;
    }

    const saved = localStorage.getItem(`inviteTableDraft_${event.id}`);
    const mobileMinLength = 5;
    const desktopMinLength = 10;
    const minLength = isDesktop ? desktopMinLength : mobileMinLength;

    let hydratedData: Row[] = [];
    let hydratedRowIds: (number | null)[] = [];

    // Hydrate from backend first
    const backendGuests = (inviteList?.filterInvitationListBy ?? [])
      .filter((item): item is NonNullable<typeof item> => !!item)
      .map((item) => ({
        id: item.id ?? "",
        first_name: item.first_name ?? "",
        last_name: item.last_name ?? "",
        email: item.email ?? "",
        phone: item.phone ?? "",
        category: item.category?.name ?? "",
      }));

    hydratedData = backendGuests.map<Row>((guest) => [
      { value: guest.first_name, error: validateCell(guest.first_name, 0) },
      { value: guest.last_name, error: validateCell(guest.last_name, 1) },
      { value: guest.email ?? "", error: null },
      { value: guest.phone, error: validateCell(guest.phone, 3) },
      { value: guest.category, error: validateCell(guest.category, 4) },
    ]);
    hydratedRowIds = backendGuests.map((g) => g.id ?? null);

    // Apply unsaved changes from local storage
    if (saved) {
      try {
        const parsedUnsaved: {
          [rowIdx: number]: { row: Row; rowId: number | null };
        } = JSON.parse(saved);

        // Find the maximum index from unsaved changes
        const maxUnsavedIdx = Math.max(
          ...Object.keys(parsedUnsaved).map(Number),
          hydratedData.length - 1
        );

        // Extend arrays to accommodate the highest index
        for (let i = hydratedData.length; i <= maxUnsavedIdx; i++) {
          hydratedData[i] = initialRow();
          hydratedRowIds[i] = null;
        }

        // Apply unsaved changes
        Object.entries(parsedUnsaved).forEach(([rowIdx, { row, rowId }]) => {
          const idx = Number(rowIdx);
          hydratedData[idx] = row;
          hydratedRowIds[idx] = rowId;
        });

        setUnsavedChanges(parsedUnsaved);
      } catch (err) {
        console.error("Failed to parse local table draft:", err);
      }
    }

    const needed = Math.max(0, minLength - hydratedData.length);
    hydratedData = [
      ...hydratedData,
      ...Array.from({ length: needed }, () => initialRow()),
    ];
    hydratedRowIds = [
      ...hydratedRowIds,
      ...Array.from({ length: needed }, () => null),
    ];

    setData(hydratedData);
    setOriginalData(JSON.parse(JSON.stringify(hydratedData)));
    setRowIds(hydratedRowIds);
    setUnsavedChanges(saved ? JSON.parse(saved) : {});
  }, [
    hydrateData,
    inviteList?.filterInvitationListBy,
    isDesktop,
    validateCell,
    event.id,
  ]);

  // useEffect(() => {
  //   // avoid saving empty arrays on initial load
  //   if (!data || data.length === 0) return;

  //   const stateToStore = {
  //     data,
  //     rowIds,
  //   };

  //   localStorage.setItem("inviteTableDraft", JSON.stringify(stateToStore));
  // }, [data, rowIds]);

  useEffect(() => {
    if (!event.id) {
      console.warn("Event ID is undefined, skipping localStorage save.");
      return;
    }

    if (Object.keys(unsavedChanges).length === 0) {
      localStorage.removeItem(`inviteTableDraft_${event.id}`);
      return;
    }

    localStorage.setItem(
      `inviteTableDraft_${event.id}`,
      JSON.stringify(unsavedChanges)
    );
  }, [unsavedChanges, event.id]);

  const addEmptyRows = () => {
    setData((prev) => [...prev, initialRow(), initialRow()]);
    setOriginalData((prev) => [...prev, initialRow(), initialRow()]);
    setRowIds((prev) => [...prev, null, null]);
  };

  // const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (!file) return;

  //   // const formData = new FormData();
  //   // formData.append("file", file);

  //   try {
  //     const data = await file.arrayBuffer();
  //     const workbook = XLSX.read(data, { type: "array" });
  //     const sheetName = workbook.SheetNames[0];
  //     const worksheet = workbook.Sheets[sheetName];
  //     const rows: string[][] = XLSX.utils.sheet_to_json(worksheet, {
  //       header: 1,
  //       defval: "", // ensures empty cells come out as ""
  //     });

  //     if (!rows.length) {
  //       toast.error("The file is empty.");
  //       return;
  //     }

  //     const expectedHeaders = [
  //       "First Name",
  //       "Last Name",
  //       "Email",
  //       "Phone",
  //       "Category",
  //     ];
  //     const headers = rows[0].map((h) => h.toString().trim());

  //     const isValid =
  //       headers.length === expectedHeaders.length &&
  //       headers.every(
  //         (h, i) => h.toLowerCase() === expectedHeaders[i].toLowerCase()
  //       );

  //     if (!isValid) {
  //       toast.error("Invalid file format. Please use the correct template.");
  //       return;
  //     }

  //     // map data to your format (skip header row)
  //     const newData: Row[] = rows.slice(1).map((row) => [
  //       { value: row[0] ?? "", error: null },
  //       { value: row[1] ?? "", error: null },
  //       { value: row[2] ?? "", error: null },
  //       { value: row[3] ?? "", error: null },
  //       { value: row[4] ?? "", error: null },
  //     ]);

  //     // hydrate your state
  //     setData(newData);
  //     setOriginalData(newData);
  //     setRowIds(newData.map(() => null));

  //     toast.success("Table successfully loaded from file.");
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Failed to read file. Please try again.");
  //   } finally {
  //     e.target.value = ""; // allows re-uploading the same file
  //   }
  // };

  const clearForm = (rowIdx: number) => {
    setData((prev) => {
      const newData = [...prev];
      newData[rowIdx] = initialRow();
      return newData;
    });
    setOriginalData((prev) => {
      const newData = [...prev];
      newData[rowIdx] = initialRow();
      return newData;
    });
    setUnsavedChanges((prev) => {
      const { [rowIdx]: _, ...rest } = prev;
      return rest;
    });
  };

  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    if (deleted && event.id) {
      setUnsavedChanges((prev) => {
        const { [guestToDelete?.id ?? -1]: _, ...rest } = prev;
        if (Object.keys(rest).length === 0) {
          localStorage.removeItem(`inviteTableDraft_${event.id}`);
        }
        return rest;
      });

      setDeleted(false);

      // localStorage.removeItem("inviteTableDraft");
    }
  }, [deleted, guestToDelete, event.id]);

  return (
    <>
      <Dialog.Root open={openDeleteGuest} onOpenChange={setOpenDeleteGuest}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed left-0 top-0 flex h-full w-full items-center justify-center gap-3 overflow-y-auto bg-[#00000080] px-4">
            <Dialog.Content>
              <RemoveGuest
                invitee={guestToDelete}
                setOpen={setOpenDeleteGuest}
                filter={invitationListFilter.not_sent}
                page={1}
                pageSize={1000}
                setDeleted={setDeleted}
              />
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="relative hidden p-4 lg:block">
        {/* <div className="absolute -top-[65px] right-[160px] flex w-fit justify-start">
          <button
            onClick={handleImportClick}
            className="rounded-md border border-gray-500 px-5 py-[10px] transition duration-300 hover:bg-primary-3 hover:text-primary-5"
          >
            Import CSV
          </button>

          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
        </div> */}
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="w-10 border bg-[#F0F0F0] px-2 py-1 text-center"></th>
              {[
                "First Name*",
                "Last Name*",
                "Email (optional)",
                "Phone*",
                "Category*",
              ].map((header, i) => (
                <th
                  key={i}
                  className="border bg-[#F0F0F0] px-2 py-1 text-center font-semibold text-primary-1"
                >
                  {header}
                </th>
              ))}
              <th className="w-10"></th> {/* Delete header*/}
            </tr>
          </thead>
          <tbody>
            {data &&
              data?.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  // onBlur={() => commitRow(rowIdx)}
                  className="focus-within:bg-gray-50"
                >
                  <td className="w-10 border bg-[#F0F0F0] p-0 text-center text-sm text-gray-600">
                    <span className="">{rowIdx + 1}</span>
                  </td>

                  {row
                    ? row.map((cell, colIdx) => (
                        <td
                          key={colIdx}
                          className="border p-0 align-top text-sm"
                        >
                          {colIdx === 4 ? (
                            <CustomSelect
                              value={cell.value}
                              onChange={(val) =>
                                handleChange(rowIdx, colIdx, val)
                              }
                              options={
                                categories?.noGuestPerCategory?.map((cat) => ({
                                  label: capitalizeFirst(
                                    cat?.category_name ?? ""
                                  ),
                                  value: cat?.category_name ?? "",
                                })) ?? []
                              }
                            />
                          ) : (
                            <div className="relative">
                              {cell.error && (
                                <div className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full border border-error-red p-2 text-error-red">
                                  !
                                </div>
                              )}
                              <input
                                type="text"
                                value={cell.value}
                                onChange={(e) =>
                                  handleChange(rowIdx, colIdx, e.target.value)
                                }
                                className={`w-full px-2 py-3 focus:outline focus:outline-1 focus:outline-primary-5 ${
                                  cell.error
                                    ? "ring-1 ring-error-red"
                                    : "border-gray-300"
                                }`}
                              />
                            </div>
                          )}
                          {cell.error && (
                            <p className="text-xs text-red-500">{cell.error}</p>
                          )}
                        </td>
                      ))
                    : null}

                  {/* Delete column */}
                  <td className="w-10 cursor-pointer border p-0 text-center transition duration-300 hover:bg-gray-200">
                    {rowIds[rowIdx] ? (
                      <div
                        onClick={() => {
                          const guest =
                            inviteList?.filterInvitationListBy?.find(
                              (g) => g?.id === rowIds[rowIdx]
                            ) ?? null;
                          setGuestToDelete(guest);
                          setOpenDeleteGuest(true);
                        }}
                        className="grid h-10 w-10 place-items-center"
                      >
                        <button className="transition hover:scale-110">
                          <Trash />
                        </button>
                      </div>
                    ) : (
                      row.some((cell) => cell.value.trim() !== "") && (
                        <div
                          onClick={() => {
                            clearForm(rowIdx);
                          }}
                          className="grid h-10 w-10 place-items-center"
                        >
                          <button className="transition hover:scale-110">
                            {updatingRowIdx === rowIdx ? (
                              <Spinner />
                            ) : (
                              <Icon iconName="close3" />
                            )}
                          </button>
                        </div>
                      )
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-start">
          <div>
            <Button
              label="add row"
              type="button"
              design="primary"
              onClick={addEmptyRows}
              className="flex items-center gap-[0.7rem] !text-white"
            >
              <Icon iconName="add" />
            </Button>
          </div>
        </div>
      </div>

      {/* For mobile */}
      <div className="block px-6 pb-12 pt-4 lg:hidden">
        <div className="mb-2 flex items-center justify-between">
          <p className="font-semibold">Invites</p>
          <p className="font-semibold">({data?.length})</p>
        </div>

        {mobileToastMsg && (
          <div
            className={`fixed left-1/2 top-6 -translate-x-1/2 transform rounded border-[0.5px] border-dashed px-4 py-2 text-xs transition-all duration-500 ease-out ${toastVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"} ${
              mobileToastType === "success"
                ? "border-[#1FAD3E] bg-[#E9FBED] text-[#34544F]"
                : mobileToastType === "error"
                  ? "border-[#B81414] bg-[#FCE8E8] text-[#8A0F0F]"
                  : "border-yellow-500 bg-white text-yellow-600"
            } z-50`}
          >
            {mobileToastMsg}
          </div>
        )}

        {data.map((row, rowIdx) => (
          <div
            key={rowIdx}
            className={`mb-4 rounded-md border border-gray-300 transition-all duration-300 hover:ring-1 hover:ring-gray-500 ${
              openRowIdx === rowIdx
                ? "bg-gray-50 shadow-lg ring-1 ring-gray-500"
                : ""
            }`}
          >
            <div
              className="cursor-pointer px-4 py-6"
              onClick={() =>
                setOpenRowIdx(openRowIdx === rowIdx ? null : rowIdx)
              }
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-4">
                  <div className="grid h-8 w-8 place-items-center border border-[#CCCCCC99] bg-[#F0F0F099]">
                    {rowIdx + 1}
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-4">
                      <div className="flex flex-col ivm:flex-row ivm:gap-1">
                        {row[0].value || row[1].value ? (
                          <>
                            <span className="max-w-[80px] truncate text-sm font-semibold xs:max-w-none xs:whitespace-normal">
                              {`${row[0].value}`}{" "}
                            </span>
                            <span className="max-w-[80px] truncate text-sm font-semibold xs:max-w-none xs:whitespace-normal">
                              {` ${row[1].value}`}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm font-semibold">
                            Add Name
                          </span>
                        )}
                      </div>

                      <div className="flex h-6 items-center justify-center rounded-full border border-[#E4E4E7] px-2 text-xs text-gray-500">
                        <p className="max-w-[80px] truncate whitespace-nowrap xs:max-w-none xs:whitespace-normal">
                          {capitalizeFirst(row[4].value ?? "") || "Regular"}
                        </p>
                      </div>
                    </div>
                    <p className="max-w-[150px] truncate text-sm text-gray-400 xs:max-w-none xs:whitespace-normal">
                      {row[2].value || "Email"}
                    </p>
                    <p className="text-sm text-gray-400">
                      {row[3].value || "Phone"}
                    </p>
                  </div>
                </div>
                <p
                  className={`transform transition-transform duration-300 ${
                    openRowIdx === rowIdx ? "rotate-180" : ""
                  }`}
                >
                  <Icon iconName="dropdown" />
                </p>
              </div>
            </div>

            <div
              className={`grid gap-6 overflow-hidden px-4 transition-all duration-500 ease-in-out ${
                openRowIdx === rowIdx
                  ? "max-h-[1000px] overflow-visible py-4"
                  : "max-h-0"
              }`}
            >
              {/* first name */}
              <div>
                <input
                  type="text"
                  value={row[0].value}
                  onChange={(e) => handleChange(rowIdx, 0, e.target.value)}
                  placeholder="Enter First Name"
                  className={`w-full rounded-lg border border-gray-300 bg-white p-3 text-sm outline-none focus:outline-1 focus:outline-[#34544f] ${
                    row[0].error ? "border-error-red" : "border-[#f2f2f2]"
                  }`}
                />
                {row[0].error && (
                  <p className="text-xs text-red-500">{row[0].error}</p>
                )}
              </div>

              {/* last name */}
              <div>
                <input
                  type="text"
                  value={row[1].value}
                  onChange={(e) => handleChange(rowIdx, 1, e.target.value)}
                  placeholder="Enter Last Name"
                  className={`w-full rounded-lg border border-gray-300 bg-white p-3 text-sm outline-none focus:outline-1 focus:outline-[#34544f] ${
                    row[1].error ? "border-error-red" : "border-[#f2f2f2]"
                  }`}
                />
                {row[1].error && (
                  <p className="text-xs text-red-500">{row[1].error}</p>
                )}
              </div>

              {/* email */}
              <div>
                <input
                  type="email"
                  value={row[2].value}
                  onChange={(e) => handleChange(rowIdx, 2, e.target.value)}
                  placeholder="Enter Email (optional)"
                  className={`w-full rounded-lg border border-gray-300 bg-white p-3 text-sm outline-none focus:outline-1 focus:outline-[#34544f] ${
                    row[2].error ? "border-error-red" : "border-[#f2f2f2]"
                  }`}
                />
                {row[2].error && (
                  <p className="text-xs text-red-500">{row[2].error}</p>
                )}
              </div>

              {/* phone */}
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={row[3].value}
                  onChange={(e) => handleChange(rowIdx, 3, e.target.value)}
                  placeholder="Enter Phone Number"
                  className={`w-full rounded-lg border border-gray-300 bg-white p-3 text-sm outline-none focus:outline-1 focus:outline-[#34544f] ${
                    row[3].error ? "border-error-red" : "border-[#f2f2f2]"
                  }`}
                />
                {row[3].error && (
                  <p className="text-xs text-red-500">{row[3].error}</p>
                )}
              </div>

              {/* category */}
              <div>
                <CustomSelect
                  options={
                    categories?.noGuestPerCategory?.map((cat, i) => ({
                      label: capitalizeFirst(cat?.category_name ?? ""),
                      value: cat?.category_name ?? "",
                    })) ?? []
                  }
                  value={row[4].value}
                  onChange={(val) => handleChange(rowIdx, 4, val)}
                  placeholder="Please select a category"
                  border
                />

                {row[4].error && (
                  <p className="text-xs text-red-500">{row[4].error}</p>
                )}
              </div>

              <div className="relative mt-4 flex items-center justify-center gap-4">
                <button
                  onClick={() => setOpenRowIdx(null)}
                  className="rounded-md bg-primary-5 px-4 py-2 text-sm text-white transition hover:bg-primary-1"
                >
                  Minimize
                </button>

                {!rowIds[rowIdx] && (
                  <button
                    onClick={() => {
                      clearForm(rowIdx);
                    }}
                    className="rounded-md border border-gray-500 px-4 py-2 text-sm text-gray-700 transition duration-300 hover:bg-gray-200"
                  >
                    Clear form
                  </button>
                )}

                {rowIds[rowIdx] && (
                  <button
                    onClick={() => {
                      const guest =
                        inviteList?.filterInvitationListBy?.find(
                          (g) => g?.id === rowIds[rowIdx]
                        ) ?? null;
                      setGuestToDelete(guest);
                      setOpenDeleteGuest(true);
                    }}
                    className="absolute left-0 grid place-items-center transition hover:bg-gray-200"
                  >
                    <Trash />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        <div className="mt-4 flex justify-end">
          <div>
            <Button
              label="add row"
              type="button"
              design="primary"
              onClick={addEmptyRows}
              className="flex items-center gap-[0.7rem] !text-white"
            >
              <Icon iconName="add" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InviteSheet;
